<template>
  <v-card class="conversation-panel" :height="height">
    <v-card-title class="conversation-header">
      <v-icon left color="primary">mdi-robot</v-icon>
      Game Designer Assistant
      <v-spacer></v-spacer>
      <v-btn icon small @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="conversation-body" ref="conversationBody">
      <div class="messages-container">
        <!-- Welcome message -->
        <div v-if="messages.length === 0" class="welcome-message">
          <v-alert type="info" outlined>
            <strong>Welcome to the Game Designer!</strong><br>
            I'm here to help you create amazing games. Tell me about your game idea, and I'll help you build it step by step.
          </v-alert>
        </div>

        <!-- Conversation messages -->
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['message-wrapper', message.type]"
        >
          <div class="message-avatar">
            <v-avatar size="32" :color="message.type === 'user' ? 'primary' : 'secondary'">
              <v-icon small :color="message.type === 'user' ? 'white' : 'white'">
                {{ message.type === 'user' ? 'mdi-account' : 'mdi-robot' }}
              </v-icon>
            </v-avatar>
          </div>

          <div class="message-content">
            <div class="message-bubble" :class="message.type">
              <div v-html="formatMessage(message.content)"></div>

              <!-- Show blueprint suggestions if any -->
              <div v-if="message.suggestions && message.suggestions.length > 0" class="suggestions mt-2">
                <v-chip
                  v-for="suggestion in message.suggestions"
                  :key="suggestion"
                  small
                  color="blue lighten-4"
                  class="mr-1 mb-1"
                  @click="applySuggestion(suggestion)"
                >
                  {{ suggestion }}
                </v-chip>
              </div>

              <!-- Show blueprint updates if any -->
              <div v-if="message.blueprintUpdates && message.blueprintUpdates.length > 0" class="updates mt-2">
                <v-alert type="success" dense outlined class="mb-2">
                  <strong>Blueprint Updated!</strong>
                </v-alert>
                <div v-for="update in message.blueprintUpdates" :key="update.section" class="update-item">
                  <v-icon small color="success">mdi-check-circle</v-icon>
                  {{ formatUpdateDescription(update) }}
                </div>
              </div>
            </div>

            <div class="message-time">
              {{ formatTime(message.timestamp) }}
            </div>
          </div>
        </div>

        <!-- Typing indicator -->
        <div v-if="isTyping" class="message-wrapper assistant">
          <div class="message-avatar">
            <v-avatar size="32" color="secondary">
              <v-icon small color="white">mdi-robot</v-icon>
            </v-avatar>
          </div>
          <div class="message-content">
            <div class="message-bubble assistant typing">
              <div class="typing-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="conversation-input">
      <v-text-field
        v-model="inputMessage"
        label="Tell me about your game idea..."
        outlined
        dense
        hide-details
        :disabled="isTyping"
        @keyup.enter="sendMessage"
        append-icon="mdi-send"
        @click:append="sendMessage"
      ></v-text-field>

      <v-btn
        icon
        color="primary"
        :disabled="!inputMessage.trim() || isTyping"
        @click="sendMessage"
      >
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </v-card-actions>

    <!-- Quick suggestion buttons -->
    <div class="quick-suggestions" v-if="showQuickSuggestions">
      <v-card outlined class="pa-3">
        <div class="subtitle-2 mb-2">Quick Start:</div>
        <div class="d-flex flex-wrap">
          <v-btn
            v-for="suggestion in quickSuggestions"
            :key="suggestion.text"
            small
            outlined
            class="mr-2 mb-2"
            @click="useQuickSuggestion(suggestion)"
          >
            {{ suggestion.text }}
          </v-btn>
        </div>
      </v-card>
    </div>
  </v-card>
</template>

<script>
import {
  sendConversationMessage,
  classifyIntent,
  generateConversationContext,
  processLLMResponse,
  applyBlueprintUpdates,
  generateConversationSuggestions
} from '@/services/conversationService'

export default {
  name: 'ConversationPanel',

  props: {
    blueprint: {
      type: Object,
      required: true
    },
    height: {
      type: String,
      default: '600px'
    },
    showQuickSuggestions: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      messages: [],
      inputMessage: '',
      isTyping: false,
      conversationHistory: []
    }
  },

  computed: {
    quickSuggestions() {
      return [
        { text: 'Create a platformer game', type: 'CREATE_GAME' },
        { text: 'Add jumping mechanics', type: 'EDIT_GAME' },
        { text: 'Design the first level', type: 'EDIT_GAME' },
        { text: 'Add collectible items', type: 'EDIT_GAME' },
        { text: 'Help with game rules', type: 'GET_HELP' }
      ]
    }
  },

  methods: {
    async sendMessage() {
      if (!this.inputMessage.trim() || this.isTyping) return

      const userMessage = this.inputMessage.trim()
      this.inputMessage = ''

      // Add user message to conversation
      this.addMessage('user', userMessage)

      // Classify intent
      const intent = classifyIntent(userMessage)

      // Generate context for LLM
      const context = generateConversationContext(this.blueprint, this.conversationHistory)

      // Show typing indicator
      this.isTyping = true

      try {
        // Send to LLM
        const response = await sendConversationMessage(userMessage, this.blueprint, this.conversationHistory)

        if (response.success) {
          // Process LLM response
          const processed = processLLMResponse(response.response, this.blueprint)

          // Add assistant message
          this.addMessage('assistant', processed.response, {
            suggestions: generateConversationSuggestions(this.blueprint),
            blueprintUpdates: processed.updates
          })

          // Apply blueprint updates if any
          if (processed.updates && processed.updates.length > 0) {
            const updatedBlueprint = applyBlueprintUpdates(this.blueprint, processed.updates)
            this.$emit('blueprint-updated', updatedBlueprint)
          }

          // Update conversation history
          this.conversationHistory.push({
            role: 'user',
            content: userMessage
          })
          this.conversationHistory.push({
            role: 'assistant',
            content: processed.response
          })
        } else {
          // Handle error
          let errorMessage = response.response || 'Sorry, I encountered an error. Please try again.'

          // Provide specific guidance for different error types
          if (response.error === 'CORS_ERROR') {
            errorMessage += '\n\n💡 **To fix this:**\n1. Make sure Firebase Functions emulator is running\n2. Run: `firebase emulators:start --only functions`\n3. Wait for it to start (it will say "Functions emulator started")\n4. Check that your GEMINI_API_KEY is configured as a Firebase secret'
          } else if (response.error === 'CONNECTION_ERROR') {
            errorMessage += '\n\n💡 **To fix this:**\n1. Open a terminal in your project\n2. Run: `firebase emulators:start --only functions`\n3. Wait for it to start (it will say "Functions emulator started")\n4. Try again!'
          }

          this.addMessage('assistant', errorMessage, {
            error: true
          })
        }
      } catch (error) {
        console.error('Conversation error:', error)
        this.addMessage('assistant', 'Oops! Something went wrong. Please try again.', {
          error: true
        })
      } finally {
        this.isTyping = false
      }
    },

    addMessage(type, content, metadata = {}) {
      this.messages.push({
        type,
        content,
        timestamp: new Date(),
        ...metadata
      })

      // Scroll to bottom
      this.$nextTick(() => {
        if (this.$refs.conversationBody) {
          this.$refs.conversationBody.scrollTop = this.$refs.conversationBody.scrollHeight
        }
      })
    },

    useQuickSuggestion(suggestion) {
      this.inputMessage = suggestion.text
      this.sendMessage()
    },

    applySuggestion(suggestion) {
      this.inputMessage = suggestion
      this.sendMessage()
    },

    formatMessage(content) {
      // Simple markdown-like formatting
      return content
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>')
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatUpdateDescription(update) {
      switch (update.section) {
        case 'objectives':
          return `Added objective: ${update.value}`
        case 'controls':
          return `Updated controls: ${JSON.stringify(update.value)}`
        case 'meta':
          return `Updated game info: ${JSON.stringify(update.value)}`
        default:
          return `Updated ${update.section}`
      }
    },

    clearConversation() {
      this.messages = []
      this.conversationHistory = []
      this.addMessage('assistant', 'Conversation cleared. What would you like to create?')
    },

    exportConversation() {
      const conversationData = {
        messages: this.messages,
        blueprint: this.blueprint,
        exportedAt: new Date().toISOString()
      }

      const dataStr = JSON.stringify(conversationData, null, 2)
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)

      const exportFileDefaultName = `conversation_${this.blueprint.meta.title || 'game'}_${new Date().toISOString().split('T')[0]}.json`

      const linkElement = document.createElement('a')
      linkElement.setAttribute('href', dataUri)
      linkElement.setAttribute('download', exportFileDefaultName)
      linkElement.click()
    }
  },

  mounted() {
    // Add welcome message if no messages exist
    if (this.messages.length === 0) {
      this.addMessage('assistant', 'Hi! I\'m your game design assistant. Tell me about the game you want to create, and I\'ll help you build it step by step!')
    }
  }
}
</script>

<style lang="scss" scoped>
.conversation-panel {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
}

.conversation-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.conversation-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: #fafafa;
}

.messages-container {
  height: 100%;
}

.message-wrapper {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;

  &.user {
    flex-direction: row-reverse;

    .message-content {
      align-items: flex-end;
    }
  }

  &.assistant {
    .message-content {
      align-items: flex-start;
    }
  }
}

.message-avatar {
  margin: 0 8px;
}

.message-content {
  max-width: 70%;
  display: flex;
  flex-direction: column;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;

  &.user {
    background-color: #2196F3;
    color: white;
    border-bottom-right-radius: 4px;
  }

  &.assistant {
    background-color: white;
    color: #333;
    border: 1px solid #e0e0e0;
    border-bottom-left-radius: 4px;
  }

  &.typing {
    padding: 16px;
  }
}

.message-time {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  padding: 0 4px;
}

.typing-dots {
  display: flex;
  gap: 4px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #999;
    animation: typing 1.4s infinite ease-in-out;

    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.conversation-input {
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  background-color: white;
}

.quick-suggestions {
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  background-color: #f8f9fa;
}

.suggestions {
  margin-top: 8px;
}

.updates {
  margin-top: 8px;

  .update-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    margin-bottom: 4px;
  }
}

.welcome-message {
  margin-bottom: 16px;
}
</style>
